import React, { useCallback, useEffect, useRef, useState } from 'react';
import Layout from '../components/molecules/Layout';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import clsx from 'clsx';
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha,
} from 'react-google-recaptcha-v3';
import { IContactForm } from '../types/contactForm.types';
import { contactSchema } from '../schemas/contact.schema';
import { submitContactForm } from '../services/contact';
import useToast from '../hooks/useToast';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';
// Give up waiting on a reCAPTCHA script that is never going to arrive (blocked, or Google down).
const RECAPTCHA_WAIT_MS = 10000;
// A token request that hangs must not strand the submission, so cap it too.
const RECAPTCHA_TOKEN_TIMEOUT_MS = 5000;
const RECAPTCHA_UNAVAILABLE_MESSAGE =
  'Our spam check could not load, which is usually an ad blocker or privacy extension. ' +
  'Please allow it for this page and reload, or email us at info@logolounge.com.';

interface ContactFormProps {
  executeRecaptcha?: (action: string) => Promise<string>;
  awaitingRecaptcha?: boolean;
}

const ContactForm = ({
  executeRecaptcha,
  awaitingRecaptcha = false,
}: ContactFormProps) => {
  const [message, setMessage] = useState('');
  const toast = useToast();
  const honeypotRef = useRef<HTMLInputElement>(null);

  const {
    handleSubmit,
    register,
    reset,
    formState: { isSubmitting },
  } = useForm<IContactForm>({
    resolver: yupResolver(contactSchema),
  });

  const onSubmit = useCallback(
    async (values: IContactForm) => {
      setMessage('');
      values.website = honeypotRef.current?.value ?? '';

      // Honeypot check: if the hidden field has a value, silently "succeed"
      if (honeypotRef.current && honeypotRef.current.value) {
        toast.addToast({
          type: 'success',
          message: 'Message sent 📤!',
        });
        return;
      }

      // Cap the token request as well as the script load: a reCAPTCHA that hangs would otherwise
      // leave the submission waiting forever with no feedback.
      if (executeRecaptcha) {
        try {
          values.recaptchaToken = await Promise.race([
            executeRecaptcha('contact_form'),
            new Promise<undefined>(resolve =>
              setTimeout(() => resolve(undefined), RECAPTCHA_TOKEN_TIMEOUT_MS)
            ),
          ]);
        } catch (error) {
          values.recaptchaToken = undefined;
        }
      }

      // The API requires the token, so a generic failure toast here tells the visitor nothing they
      // can act on. Name the likely cause and give them a way through instead.
      if (!values.recaptchaToken) {
        setMessage(RECAPTCHA_UNAVAILABLE_MESSAGE);
        return;
      }

      try {
        const response = await submitContactForm(values);
        if (response === 'success') {
          toast.addToast({
            type: 'success',
            message: 'Message sent 📤!',
          });
          reset();
        } else {
          toast.addToast({
            type: 'error',
            message: 'There was an error, try again later.',
          });
        }
      } catch (error) {
        toast.addToast({
          type: 'error',
          message: 'There was an error, try again later.',
        });
      }
    },
    [executeRecaptcha, toast, reset]
  );

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="support_form"
      method="post"
      action=""
    >
      <input type="hidden" name="User" value="0" />

      {/* Honeypot field - hidden from real users, bots will fill it in */}
      <div aria-hidden="true" className="honeypot-field">

        <label htmlFor="website">Website</label>
        <input
          type="text"
          id="website"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          ref={honeypotRef}
        />
      </div>

      <div className="form-group">
        <label className="control-label">Name</label>
        <input className="form-control" type="text" {...register('name')} />
      </div>

      <div className="form-group">
        <label className="control-label">Email</label>
        <input className="form-control" type="email" {...register('email')} />
      </div>

      <div className="form-group">
        <label className="control-label">Subject</label>
        <select className="form-control" {...register('subject')}>
          <option>General Contact</option>
          <option>Account Help</option>
        </select>
      </div>

      <p id="bug_report_message" className="field_message hidden">
        Got a problem with the website? For starters, try{' '}
        <a href="logout">logging out</a> and then logging back in again.
      </p>

      <div className="form-group">
        <label className="control-label">Message</label>
        <textarea
          required
          className="form-control"
          placeholder="How can we help?"
          {...register('message')}
        ></textarea>
      </div>

      <div className="button_outer">
        <button
          type="submit"
          className="btn btn-primary btn--has-arrow"
          disabled={awaitingRecaptcha || isSubmitting}
          aria-busy={awaitingRecaptcha || isSubmitting}
        >
          {awaitingRecaptcha ? 'Checking…' : isSubmitting ? 'Sending…' : 'Send Message'}
        </button>
        {awaitingRecaptcha && (
          <p className="recaptcha_loading_hint">
            Waiting for the spam check to load…
          </p>
        )}
      </div>
      {message && (
        <div className="button_outer">
          <p className="recaptcha_unavailable_notice">{message}</p>
        </div>
      )}
    </form>
  );
};

const RecaptchaContactForm = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [recaptchaTimedOut, setRecaptchaTimedOut] = useState(false);

  // Submitting before the script loads sends no token, but waiting forever is worse than no token.
  useEffect(() => {
    if (executeRecaptcha) return;
    const timer = setTimeout(() => setRecaptchaTimedOut(true), RECAPTCHA_WAIT_MS);
    return () => clearTimeout(timer);
  }, [executeRecaptcha]);

  return (
    <ContactForm
      executeRecaptcha={executeRecaptcha ?? undefined}
      awaitingRecaptcha={!executeRecaptcha && !recaptchaTimedOut}
    />
  );
};

const Contact = () => {
  useEffect(() => {
    document.title =
      'Contact - LogoLounge - Logo Design Inspiration & Competition - Work';
  }, []);

  return (
    <Layout className={clsx('support has_left_background')} showRibbon={true}>
      <main id="content">
        <figure></figure>
        <article
          className="article"
          itemScope={false}
          itemType="http://schema.org/Article"
        >
          <div className="content_outer">
            <h1>Contact</h1>

            {RECAPTCHA_SITE_KEY ? (
              <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
                <RecaptchaContactForm />
              </GoogleReCaptchaProvider>
            ) : (
              <ContactForm />
            )}
          </div>
        </article>
      </main>
    </Layout>
  );
};

export default Contact;
